import { atom, useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { store } from '.'

const INTRO_STORAGE_KEY = 'intro-dialogs'

export const INTRO_DIALOGS = {
  ACTIVE_ASSORTMENTS: 'active-assortment',
  ASSORTMENTS: 'assortment',
  AUDIT_TRAILS: 'audit-trails',
  ALL_ORDERS: 'all-orders',
  BUDGET_TRACKER: 'budget-tracker',
  FLAGGED_ORDERS: 'flagged-orders',
  INVITATIONS: 'invitations',
  LAUNCHES: 'launches',
  MISSING_ORDERS: 'missing-orders',
  MY_UPLOADS: 'my-uploads',
  REPORTS: 'reports',
  RETURNS: 'returns',
  ROLES: 'roles',
  STORE_DETAILS: 'store-details',
  STORES: 'stores',
  UPLOAD_DATA: 'upload-data',
  UPLOAD_DETAILS_APP: 'upload-details-app',
  UPLOAD_DETAILS_CORE: 'upload-details-core',
  USERS: 'users',
  WELCOME: 'welcome',
} as const

export type IntroDialog = (typeof INTRO_DIALOGS)[keyof typeof INTRO_DIALOGS]

const createDefaultIntroDialogs = (): Record<IntroDialog, boolean> => {
  return Object.values(INTRO_DIALOGS).reduce(
    (acc, id) => {
      acc[id] = true
      return acc
    },
    {} as Record<IntroDialog, boolean>,
  )
}

export const introDialogsAtom = atomWithStorage<Record<IntroDialog, boolean>>(
  INTRO_STORAGE_KEY,
  createDefaultIntroDialogs(),
  undefined,
  {
    getOnInit: true,
  },
)

// Track if the storage atom has been loaded
export const introDialogsLoadedAtom = atom(false)

export const resetAllIntroDialogs = () => {
  store.set(introDialogsAtom, createDefaultIntroDialogs())
}

export const useIntroDialogs = (dialogId: IntroDialog) => {
  const [introDialogs, setIntroDialogs] = useAtom(introDialogsAtom)

  const shouldShowIntroDialog = () => {
    return introDialogs[dialogId] ?? true
  }

  const disableIntroDialog = () => {
    setIntroDialogs({ ...introDialogs, [dialogId]: false })
  }

  const showIntroDialog = () => {
    setIntroDialogs({ ...introDialogs, [dialogId]: true })
  }

  const dismissAllIntroDialogs = () => {
    const allDialogIds = Object.values(INTRO_DIALOGS)
    const dismissedDialogs = allDialogIds.reduce(
      (acc, id) => {
        acc[id] = false
        return acc
      },
      {} as Record<IntroDialog, boolean>,
    )
    setIntroDialogs(dismissedDialogs)
  }

  return {
    shouldShowIntroDialog,
    disableIntroDialog,
    showIntroDialog,
    dismissAllIntroDialogs,
    resetAllIntroDialogs,
  }
}
